import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

/**
 * Exports data to an Excel file.
 * Can handle single or multiple sheets.
 * @param {Array<Object>} sheets - An array of sheet definition objects.
 *        Each object must have `name` (string) and `data` (Array<Array<any>> or Array<Object>) properties.
 *        Optionally, a `dataType` ('aoa' or 'json') property can be on each sheetDef to override default.
 * @param {string} baseFileName - The base name for the Excel file (e.g., "OrdersExport").
 *        A timestamp will be appended automatically.
 * @param {string} [defaultDataType='aoa'] - The default data type for sheet data if not specified per sheet
 *        by a `dataType` property on the sheet definition object. Valid values: 'aoa', 'json'.
 * @param {number} [itemCountForMessage] - Optional. The number of items (e.g., orders) actually being exported,
 *        used for a more specific success message.
 * @returns {boolean} - True if export was initiated successfully, false otherwise.
 */
export const exportDataToExcel = (sheets, baseFileName, defaultDataType = 'aoa', itemCountForMessage) => {
    if (!Array.isArray(sheets) || sheets.length === 0) {
        toast.info('No data provided for export.');
        return false;
    }

    try {
        const workbook = XLSX.utils.book_new();
        let sheetsAdded = 0;

        for (const sheetDef of sheets) {
            if (typeof sheetDef !== 'object' || sheetDef === null ||
                typeof sheetDef.name !== 'string' || !Array.isArray(sheetDef.data)) {
                console.warn('Skipping invalid sheet definition:', sheetDef);
                toast.warn(`Skipping sheet due to invalid format: ${sheetDef?.name || 'Unknown'}`);
                continue;
            }

            const currentDataType = sheetDef.dataType || defaultDataType;
            let worksheet;

            if (currentDataType === 'aoa') {
                // Ensure data is an array of arrays for aoa_to_sheet
                if (sheetDef.data.length === 0 || (sheetDef.data.length > 0 && Array.isArray(sheetDef.data[0]))) {
                    worksheet = XLSX.utils.aoa_to_sheet(sheetDef.data);
                } else {
                    console.error(`Data for sheet "${sheetDef.name}" is not an array of arrays as expected for 'aoa' type.`);
                    toast.error(`Invalid data format for sheet "${sheetDef.name}" (expected array of arrays).`);
                    continue;
                }
            } else if (currentDataType === 'json') {
                 // Ensure data is an array of objects for json_to_sheet
                if (sheetDef.data.length === 0 || (sheetDef.data.length > 0 && typeof sheetDef.data[0] === 'object' && sheetDef.data[0] !== null && !Array.isArray(sheetDef.data[0]))) {
                    worksheet = XLSX.utils.json_to_sheet(sheetDef.data);
                } else {
                    console.error(`Data for sheet "${sheetDef.name}" is not an array of objects as expected for 'json' type.`);
                    toast.error(`Invalid data format for sheet "${sheetDef.name}" (expected array of objects).`);
                    continue;
                }
            } else {
                toast.error(`Unsupported data type "${currentDataType}" for sheet "${sheetDef.name}".`);
                console.error(`Unsupported data type "${currentDataType}" for sheet "${sheetDef.name}".`);
                continue;
            }
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetDef.name);
            sheetsAdded++;
        }

        if (sheetsAdded === 0) {
            toast.error('No valid sheets could be added to the Excel file.');
            return false;
        }

        const fullFileName = `${baseFileName}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fullFileName);

        let itemsMessage = 'data';
        if (itemCountForMessage) {
            itemsMessage = `${itemCountForMessage} ${itemCountForMessage === 1 ? 'item' : 'items'}`;
            // Make message more specific if it's a single order export
            if (itemCountForMessage === 1 && baseFileName.toLowerCase().includes("order")) {
                itemsMessage = '1 order';
            }
        }
        toast.success(`Successfully exported ${itemsMessage} to ${fullFileName}!`);
        return true;

    } catch (error) {
        console.error('Error exporting to Excel:', error);
        toast.error(error.response?.data?.message || error.message || 'Failed to export data. Please try again.');
        return false;
    }
};
